import React, { useEffect, useRef } from 'react';
import send from '../../../../assets/send.svg';
import microphone from '../../../../assets/microphone.svg';
import audioAttach from '../../../../assets/audio-attach.svg';
import WaveformVisualizer from './WaveformVisualizer';
import { useBrand } from '../../../../brands/contexts/BrandContext';
import { useI18n } from '../../../contexts/I18nContext';

interface ChatInputProps {
  inputValue: string;
  setInputValue: (val: string) => void;
  isRecording: boolean;
  recordingSeconds: number;
  isTranscribing: boolean;
  isGeneratingProtocol: boolean;
  isProtocolButtonActive: boolean;
  isSearchingSpecialities?: boolean;
  isWaitingForFinalResults?: boolean;
  hasGeneratedProtocols?: boolean;
  onInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  onPrimaryButtonClick: () => void;
  onCancelRecording: () => void;
  onGenerateProtocol: (selectedSpeciality?: string) => void;
  onAudioUpload: () => void;
  onSendToMIS?: () => void;
  formatTime: (seconds: number) => string;
  lastRecognizedText: string;
  interimText: string;
  audioStream: MediaStream | null;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputValue,
  setInputValue,
  isRecording,
  recordingSeconds,
  isTranscribing,
  isGeneratingProtocol,
  isProtocolButtonActive,
  isSearchingSpecialities,
  isWaitingForFinalResults,
  hasGeneratedProtocols = false,
  onInput,
  onSend,
  onPrimaryButtonClick,
  onCancelRecording,
  onGenerateProtocol,
  onAudioUpload,
  onSendToMIS,
  formatTime,
  lastRecognizedText,
  interimText,
  audioStream,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { brandId } = useBrand();
  const { t } = useI18n();

  // Автоматически изменяем высоту textarea при изменении содержимого
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [inputValue]);

  return (
    <div id="input-container" style={{ position: 'relative' }}>
      <style>
        {`
        @keyframes fadeInWord {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}
      </style>
      {isRecording && interimText && (
        <div style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: '100%',
          background: '#fff',
          borderRadius: '12px',
          padding: '12px',
          marginBottom: '8px',
          minHeight: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          lineHeight: '0.9',
          fontSize: '14px',
          fontWeight: 400,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          alignItems: 'center',
          zIndex: 10,
          pointerEvents: 'none',
          transition: 'all 0.2s',
        }}>
          {interimText.split(' ').map((word, idx) => (
            <span key={idx} style={{
              opacity: 0,
              animation: 'fadeInWord 0.4s forwards',
              animationDelay: `${idx * 0.08}s`,
              marginRight: 2,
            }}>{word}</span>
          ))}
        </div>
      )}
      <div className='d-flex flex-row align-items-center w-100 mb-2'>
        {isRecording ? (
          <div className="input-primary w-100 me-2 d-flex justify-content-between align-items-center" style={{ height: "38px", padding: "8px 12px" }}>
            <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
              <span className="recording-dot"></span>
              <span style={{ minWidth: "45px" }}>{formatTime(recordingSeconds)}</span>
              <WaveformVisualizer audioStream={audioStream} isRecording={isRecording} />
            </div>
            <button className="btn btn-link text-decoration-none text-danger" onClick={onCancelRecording} style={{ flexShrink: 0 }}>{t('dialogs.recordingCancel')}</button>
          </div>
        ) : isWaitingForFinalResults ? (
          <div className="input-primary w-100 me-2 d-flex justify-content-center align-items-center" style={{ height: "38px" }}>
            <span className="loader-dots">{t('dialogs.finishingRecord')}</span>
          </div>
        ) : (
          <div className="input-container w-100 me-2" style={{ position: 'relative' }}>
            <textarea
              ref={textareaRef}
              className='input-primary w-100 mt-2'
              rows={1}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onInput={onInput}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
              placeholder={t('dialogs.input.placeholder')}
              disabled={isTranscribing || isGeneratingProtocol || isWaitingForFinalResults}
              style={{
                resize: 'none',
                whiteSpace: 'pre-wrap',
                overflowY: 'auto',
                paddingRight: inputValue.trim().length === 0 ? '40px' : '24px', // Динамический отступ
                minHeight: '56px',
                maxHeight: '200px',
              }}
            ></textarea>
            {inputValue.trim().length === 0 && (
              <button
                className="audio-attach-button-inline"
                onClick={onAudioUpload}
                disabled={isTranscribing || isGeneratingProtocol || isWaitingForFinalResults}
                title={t('dialogs.attachAudio')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-48%)',
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  opacity: isTranscribing || isGeneratingProtocol || isWaitingForFinalResults ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                }}
              >
                <img src={audioAttach} alt="Attach Audio" style={{ width: '18px', height: '18px' }} />
              </button>
            )}
          </div>
        )}
        <button
          className={`button-primary send-button`}
          onClick={onPrimaryButtonClick}
          disabled={isGeneratingProtocol || isWaitingForFinalResults}
        >
          {(inputValue.trim() || lastRecognizedText.trim()) ? (
            <img src={send} alt="Send" />
          ) : (
            <img src={isRecording ? send : microphone} alt="Record/Send" />
          )}
        </button>
      </div>
      <div className="d-flex gap-2">
        <button
          className={`flex-grow-1 button-primary mb-3 ${(isTranscribing || isGeneratingProtocol || isSearchingSpecialities || isWaitingForFinalResults || !isProtocolButtonActive) ? 'disabled' : ''}`}
          onClick={() => onGenerateProtocol()}
          disabled={isTranscribing || isGeneratingProtocol || isSearchingSpecialities || isWaitingForFinalResults || !isProtocolButtonActive}
        >
          {isTranscribing ? (
            <span className="loader-dots">{t('dialogs.transcribing')}</span>
          ) : isWaitingForFinalResults ? (
            <span className="loader-dots">{t('dialogs.finishingRecord')}</span>
          ) : isSearchingSpecialities ? (
            <span className="loader-dots">{t('dialogs.searchingSpecialty')}</span>
          ) : isGeneratingProtocol ? (
            <span className="loader-dots">{t('dialogs.aiTyping')}</span>
          ) : (
            t('dialogs.generateProtocol')
          )}
        </button>
        {brandId === 'budzdorov' && hasGeneratedProtocols && onSendToMIS && (
          <button
            className="button-primary mb-3"
            onClick={onSendToMIS}
            disabled={isTranscribing || isGeneratingProtocol || isSearchingSpecialities || isWaitingForFinalResults}
            style={{ whiteSpace: 'nowrap' }}
          >
            {t('dialogs.sendToMIS')}
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatInput; 